import { useState } from 'react';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import useTripStore from '../store/useTripStore';
import { toTripState } from '../logic/toTripState';
import {
  useCompareWithVersion,
  useSetCompareWithVersion,
} from './useTripStoreSelectors';

interface UseSwitchTripVersionParams {
  tripId: string;
  // The app refreshes what it holds of the trip (its current version) once the
  // new one is saved.
  reloadTrip: () => void;
}

/**
 * Makes another version of the trip the current one: the version is saved as
 * current, then loaded into the store. The version being compared with is
 * dropped, since the compare panel would show the version now on screen.
 */
export function useSwitchTripVersion({
  tripId,
  reloadTrip,
}: UseSwitchTripVersionParams) {
  const { api } = useTripDesignerConfig();
  const compareWithVersion = useCompareWithVersion();
  const setCompareWithVersion = useSetCompareWithVersion();
  const initializeTrip = useTripStore((state) => state.initializeTrip);
  const setInitialExtras = useTripStore((state) => state.setInitialExtras);
  const [loading, setLoading] = useState(false);

  const switchToVersion = async (version: { id: string }) => {
    setLoading(true);

    try {
      if (compareWithVersion) {
        setCompareWithVersion(null);
      }

      const trip: any = (await api.getTrip({ tripId, versionId: version.id }))
        .data;

      await api.saveTrip({
        ...trip,
        dmcId: trip.dmc?.id,
        currentVersion: { id: version.id },
      });
      reloadTrip();

      const { days, experiences, hotels, initialExtras } = await toTripState({
        trip,
        api,
        locale: trip?.locale,
      });

      initializeTrip({ days, experiences, hotels }, tripId);
      setInitialExtras(initialExtras);
    } finally {
      setLoading(false);
    }
  };

  return { switchToVersion, loading };
}
